import { DOCUMENT } from '@angular/common';
import { Component, inject, input, OnChanges, output } from '@angular/core';
import SwiperCore, { Navigation, Swiper } from 'swiper';

import { ProductFilterItem } from '../../../../menu-categories/models/menu-categories.model';

import { isNoindexCategory } from '@core/application/use-cases/seo/is-no-index-category.use-case';
import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { MenuCategory } from '@shared/header/models/category.model';

// install Swiper modules
SwiperCore.use([Navigation]);

const SPACE_BETWEEN_SWIPER = 20;
const DELAY_LOAD_SWIPER = 10;

@Component({
    selector: 'category-sub-categories-default-navigation',
    templateUrl: './category-sub-categories-default-navigation.component.html',
})
export class CategorySubCategoriesDefaultNavigationComponent implements OnChanges {
    private readonly document: Document = inject(DOCUMENT);

    private readonly platformService: CorePlatformService = inject(CorePlatformService);

    public readonly siblingCategories = input<MenuCategory[]>();

    public readonly currentCategoryFromABTest = input<MenuCategory>();

    public readonly showDebugCategoryIds = input<boolean>();

    public readonly filtersArray = input<ProductFilterItem[]>();

    public readonly isNormalBehavior = input<boolean>();

    public readonly lastSectionTranslated = input<string>();

    public readonly direction = input<string>();

    public readonly goToCategoryEmitter = output<MenuCategory>();

    public readonly updateFilterEmitter = output<string>();

    public readonly uuid = Date.now().toString();

    public readonly swiperCategoriesClass: string = `swiper-${this.uuid}`;

    public isNoindex: boolean;

    public ngOnChanges(): void {
        if (this.siblingCategories()?.length > 0) {
            const indexSiblingCategory = this.siblingCategories()
                .findIndex(category => category.id === this.currentCategoryFromABTest()?.id);
            this.initSwiper(indexSiblingCategory);
            this.isNoindex = isNoindexCategory(this.siblingCategories()[indexSiblingCategory]?.attachments);
        }
    }

    public goToCategory(category: MenuCategory, e: MouseEvent): void {
        e.stopPropagation();
        e.preventDefault();
        this.goToCategoryEmitter.emit(category);
    }

    public updateFilter(filterType: string): void {
        this.updateFilterEmitter.emit(filterType);
    }

    private initSwiper(indexSiblingCategory: number): void {
        const navigationCategoriesOptions = {
            nextEl: `.swiper-${this.uuid}-nav-right`,
            prevEl: `.swiper-${this.uuid}-nav-left`,
        };
        setTimeout(() => {
            return new Swiper(`.${this.swiperCategoriesClass}`, {
                spaceBetween: SPACE_BETWEEN_SWIPER,
                freeMode: true,
                slidesPerView: 'auto',
                navigation: navigationCategoriesOptions,
                initialSlide: indexSiblingCategory,
                slideToClickedSlide: indexSiblingCategory !== 0,
                breakpoints: {
                    768: {
                        freeMode: true,
                        slidesPerView: 'auto',
                        navigation: navigationCategoriesOptions,
                        centerInsufficientSlides: false,
                        initialSlide: indexSiblingCategory,
                        slideToClickedSlide: indexSiblingCategory !== 0,
                    },
                },
                on: {
                    afterInit: (swiper) => {
                        swiper.update();
                        if (this.platformService.isBrowser()) {
                            this.toggleNavNeededClass(
                                swiper.isLocked,
                                this.document,
                                '.swiper-sub-categories',
                            );
                        }
                    },
                },
            });
        }, DELAY_LOAD_SWIPER);
    }

    private toggleNavNeededClass(
        isLocked: boolean,
        document: Document,
        selector: string,
    ): void {
        const element = document.querySelector(selector);
        if (!element) return;

        if (isLocked) {
            element.classList.remove('nav-needed');
        } else {
            element.classList.add('nav-needed');
        }
    }
}
